// ProfileDetails.js
import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, Navigate } from "react-router-dom";
import { APP_CONFIG } from "../../app-config";
import SidebarHeader from "../../components/SidebarHeader";
import { encryptDataHybrid, apiCall } from "../../utils/api";
import toast, { Toaster } from 'react-hot-toast';
import { FaUser, FaStore } from 'react-icons/fa';
import { FiArrowLeft } from "react-icons/fi";

const COLORS = APP_CONFIG.colors;
const currentYear = new Date().getFullYear();


function Apdcl() {
    const navigate = useNavigate();
    const [amount, setAmount] = useState("");
    const [error, setError] = useState("");
    const [subDivision, setSubDivision] = useState("");
    const [consumerNumber, setConsumerNumber] = useState("");
    const [parsedData, setParsedData] = useState(JSON.parse(localStorage.getItem("credentials")));

    const subDivisions = [
        //{ name: "Kharupetia", prefix: "0830" },
        { name: "Mangaldoi", prefix: "0840" }
    ];

    const handleSubDivisionChange = (e) => {
        const selected = subDivisions.find(sd => sd.name === e.target.value);
        setSubDivision(e.target.value);
        if (selected) {
            setConsumerNumber(selected.prefix);
        }
    };

    const handleConsumerNumberChange = (e) => {
        const value = e.target.value;
        // Only allow numbers and enforce prefix
        if (value.startsWith(subDivisions.find(sd => sd.name === subDivision)?.prefix || "")) {
            setConsumerNumber(value);
        } else if (value === "") {
            setConsumerNumber(subDivisions.find(sd => sd.name === subDivision)?.prefix || "");
        }
    };

    const handlePay = () => {
        if (!subDivision) {
            setError("Please select a Sub Division");
            return;
        }

        if (!consumerNumber || consumerNumber.length !== 12) {
            setError("Consumer Number must be exactly 12 digits");
            return;
        }

        if (Number(amount) % 100 !== 0) {
            setError("Amount must be divisible by 100 (e.g., 100, 200, 300, 400...)");
            return;
        }

        navigate("/payment-confirmation", {
            state: {
                service: "Bill Payment",
                operator: 'Assam Power Distribution Company Ltd- Smart Prepaid Recharge',
                logo: 'https://static.mobikwik.com/appdata/operator_icons/op198.png',
                operator_id: '21724',
                number: consumerNumber,
                amount: amount,
                consumerName: '',
                serviceSpecificData: {
                    subDivision: subDivision,
                    consumerNumber: consumerNumber,
                    range: {
                        min_amount: '101',
                        max_amount: '3000'
                    },
                    form: {
                        param1: consumerNumber,
                        param2: subDivision
                    }
                }
            }
        });
    };

    return (
        <SidebarHeader>
            <div className="max-w-7xl mx-auto mb-16">
                <div className="flex items-center gap-3 pb-4">
                    <button
                        onClick={() => navigate(-1)}
                        className="flex items-center justify-center w-10 h-10 rounded-xl bg-gray-200 hover:bg-gray-300 transition"
                    >
                        <FiArrowLeft className="text-xl" />
                    </button>
                    <h1 className="text-lg font-semibold text-gray-800">
                        APDCL - Smart Meter 
                    </h1>
                </div>

                {/* APDCL Card with Operator Icon */}
                <div className="flex items-center gap-3 mb-6 p-3.5 px-4 rounded-xl border"
                    style={{
                        background: COLORS.surfaceColor,
                        borderColor: COLORS.borderColor,
                    }}
                >
                    <img
                        src='https://static.mobikwik.com/appdata/operator_icons/op198.png'
                        alt='APDCL'
                        className="w-11 h-11 rounded-xl object-cover flex-shrink-0"
                        onError={(e) => {
                            e.target.style.display = 'none';
                            if (e.target.nextSibling) {
                                e.target.nextSibling.style.display = 'flex';
                            }
                        }}
                    />
                    <div className="flex-1">
                        <div className="text-[15px] font-bold" style={{ color: COLORS.textPrimary }}>
                            Assam Power Distribution Company Ltd- Smart Prepaid Recharge
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    {/* Sub Division Dropdown */}
                    <div >
                        <label
                            className="block text-sm mb-2"
                            style={{ color: COLORS.textPrimary }}
                        >
                            Select Operator
                        </label>
                        <select
                            value={subDivision}
                            onChange={handleSubDivisionChange}
                            className="w-full p-3.5 rounded-xl text-[15px] outline-none transition-colors duration-200"
                            style={{
                                border: `1px solid ${error && !subDivision ? COLORS.errorColor : COLORS.borderColor}`,
                                background: COLORS.surfaceColor,
                                color: COLORS.textPrimary,
                            }}
                        >
                            <option value="">Select Sub Division</option>
                            {subDivisions.map((sd) => (
                                <option key={sd.name} value={sd.name}>
                                    {sd.name} ({sd.prefix})
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Consumer Number Input */}
                    <div>
                        <label
                            className="block text-sm mb-2"
                            style={{ color: COLORS.textPrimary }}
                        >
                            Consumer Number (12 digits)
                        </label>
                        <input
                            type="number"
                            placeholder={subDivision ? "Enter remaining 8 digits" : "Select Sub Division first"}
                            value={consumerNumber}
                            onChange={handleConsumerNumberChange}
                            disabled={!subDivision}
                            maxLength={12}
                            className="w-full p-3.5 rounded-xl text-[15px] outline-none transition-colors duration-200"
                            style={{
                                border: `1px solid ${error && consumerNumber.length !== 12 ? COLORS.errorColor : COLORS.borderColor}`,
                                background: COLORS.surfaceColor,
                                color: COLORS.textPrimary,
                                opacity: subDivision ? 1 : 0.6,
                            }}
                            onFocus={(e) => (e.target.style.borderColor = COLORS.primaryColor)}
                            onBlur={(e) => (e.target.style.borderColor = COLORS.borderColor)}
                        />
                    </div>

                    {/* Amount Input */}
                    <div>
                        <label
                            className="block text-sm mb-2"
                            style={{ color: COLORS.textPrimary }}
                        >
                            Amount
                        </label>
                        <input
                            type="number"
                            placeholder="Enter amount"
                            value={amount}
                            onChange={(e) => { setAmount(e.target.value); setError(""); }}
                            className="w-full p-3.5 rounded-xl text-[15px] outline-none transition-colors duration-200"
                            style={{
                                border: `1px solid ${error && (!amount || Number(amount) <= 100 || Number(amount) % 100 !== 0) ? COLORS.errorColor : COLORS.borderColor}`,
                                background: COLORS.surfaceColor,
                                color: COLORS.textPrimary,
                            }}
                            onFocus={(e) => (e.target.style.borderColor = COLORS.primaryColor)}
                            onBlur={(e) => (e.target.style.borderColor = COLORS.borderColor)}
                            min="100"
                            step="100"
                        />
                        <div className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                            Amount must be divisible by 100 (e.g., ₹100, ₹200, ₹300...)
                        </div>
                        {error && (
                            <div
                                className="text-xs mt-2"
                                style={{ color: COLORS.errorColor }}
                            >
                                {error}
                            </div>
                        )}
                    </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-6">
                    {/* Pay Button */}
                    <button
                        onClick={handlePay}
                        className="w-full py-3.5 rounded-lg text-base font-semibold flex items-center justify-center gap-2 transition-all duration-200 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed"
                        style={{
                            background: COLORS.primaryColor,
                            color: COLORS.textOnPrimary,
                            borderRadius: COLORS.borderRadiusBtn,
                        }}
                    >
                        Pay Bill
                    </button>
                    <div></div>
                </div>

                <Toaster />

                <style>
                    {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                `}
                </style>

            </div>
        </SidebarHeader>
    );
}

export default Apdcl;